import { useAppSelector } from '@store';
import {
  ButtonPrimary,
  ButtonPseudo,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
} from '@ui/src';
import useDocument from './hooks/use-document';
import { DocumentPanelTypes } from './types';
import DocumentItem from '../../../components/new-application/common/document-sidepanel/document-sidepanel';
import {
  ApplicationDocumentStatus,
  NewApplicationCorporateInfo,
  NewApplicationListItem,
  NewApplicationStatus,
} from '../types';

import cn from './styles.module.css';

const DocumentPanel = ({
  isCof,
  isCorporate,
  clientType,
}: DocumentPanelTypes) => {
  const {
    document: individualDocumentItem,
    newApplicationInfo: individualApplicationInfo,
    allowedDocumentsCategories: individualAllowedDocumentsCategories,
    documents: individualDocuments,
  } = useAppSelector((state) => state.newApplications.individualInfo);

  const {
    document: corporateDocumentItem,
    newApplicationInfo: corporateApplicationInfo,
    documents: corporateDocuments,
    allowedDocumentsCategories: corporateAllowedDocumentsCategories,
  } = useAppSelector((state) => state.newApplications.corporateInfo);
  const {
    document: cofDocumentItem,
    newApplicationInfo: cofApplicationInfo,
    allowedDocumentsCategories: cofAllowedDocumentsCategories,
    documents: cofDocuments,
  } = useAppSelector((state) => state.newApplications.mainRepresentative);
  const { activePermissions } = useAppSelector((state) => state.root);

  let documentItem = individualDocumentItem;
  let documents = individualDocuments;
  let newApplicationInfo:
    NewApplicationListItem | NewApplicationCorporateInfo | undefined =
    individualApplicationInfo;
  let updateAllowed =
    activePermissions?.includes(PermissionEnum.DOCUMENTS_UPDATE_ACTION) &&
    newApplicationInfo?.applicationScheme?.id ===
      'ed560559-8354-4634-b6dd-eb341dfb551a';
  let allowedDocumentsCategories = individualAllowedDocumentsCategories;

  if (isCof) {
    documentItem = cofDocumentItem;
    documents = cofDocuments;
    newApplicationInfo = cofApplicationInfo;
    updateAllowed =
      activePermissions?.includes(PermissionEnum.DOCUMENTS_UPDATE_ACTION) &&
      (newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159' ||
        newApplicationInfo?.applicationScheme?.id ===
          'ad811a02-87ed-4e19-ad45-770c0fd8fe41');
    allowedDocumentsCategories = cofAllowedDocumentsCategories;
  } else if (isCorporate) {
    documentItem = corporateDocumentItem;
    documents = corporateDocuments;
    newApplicationInfo = corporateApplicationInfo;
    updateAllowed = activePermissions?.includes(
      PermissionEnum.DOCUMENTS_UPDATE_ACTION,
    );
    allowedDocumentsCategories = corporateAllowedDocumentsCategories;
  }

  const {
    documentIsLoading,
    isLoading,
    onDownload,
    isFetching,
    imgLink,
    addCommentAction,
    commentInProgress,
    setDocumentName,
    documentName,
    nameError,
    errors,
    onChangeDocumentExpDate,
    moveInProgress,
    setDeletePopupVisible,
    changeDocumentCategoryAction,
    changeDateError,
    updateInProgress,
    onDelete,
    deleteInProgress,
    deletePopupVisible,
    panelIsOpen,
    handleClose,
    renameInProgress,
    onRename,
    statusChangeInProgress,
    selectedStatus,
    setSelectedStatus,
    changeDocumentStatus,
  } = useDocument({
    isCof,
    isCorporate,
    documentItem,
    documents,
  });

  let content;

  if (documentIsLoading) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <DocumentItem
        document={documentItem}
        type={documentItem?.category}
        documentFileIsLoading={isLoading}
        onDownload={onDownload}
        downloadInProgress={isFetching}
        applicationStatus={newApplicationInfo?.status}
        imgLink={imgLink}
        addCommentAction={addCommentAction}
        commentsVisible={activePermissions?.includes(
          PermissionEnum.DOCUMENTS_COMMENTS_ACTION,
        )}
        commentInProgress={commentInProgress}
        clientType={clientType}
        setDocumentName={setDocumentName}
        documentName={documentName}
        nameError={nameError || (errors?.name?.message as string)}
        renameAvailable={activePermissions?.includes(
          PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
        )}
        updateAllowed={updateAllowed}
        onChangeDocumentExpDate={(date) =>
          (!isCof &&
            !isCorporate &&
            newApplicationInfo?.applicationScheme?.id ===
              'ed560559-8354-4634-b6dd-eb341dfb551a') ||
          isCorporate ||
          (isCof &&
            (newApplicationInfo?.applicationScheme?.id ===
              'a87db4b4-3c78-4564-bba1-c16a19553159' ||
              newApplicationInfo?.applicationScheme?.id ===
                'a87db4b4-3c78-4564-bba1-c16a19553159'))
            ? onChangeDocumentExpDate(date)
            : undefined
        }
        moveInProgress={moveInProgress}
        setDeletePopupVisible={setDeletePopupVisible}
        onChangeCategory={changeDocumentCategoryAction}
        changeDateError={changeDateError}
        updateInProgress={updateInProgress}
        allowedCategories={allowedDocumentsCategories}
        regulatedCompanyScheme={Boolean(
          newApplicationInfo?.applicationScheme?.id ===
            'a87db4b4-3c78-4564-bba1-c16a19553159' ||
          newApplicationInfo?.applicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a',
        )}
        cofWithDocuments={Boolean(
          isCof &&
          newApplicationInfo?.applicationScheme?.id ===
            'ad811a02-87ed-4e19-ad45-770c0fd8fe41',
        )}
      />
    );
  }

  return (
    <>
      <Popup
        title={`Delete ${documentItem?.name}`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDelete}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        isLoading={deleteInProgress}
        parent={document.getElementById('delete-popup-wrapper') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={documentItem?.name || ''}
        sidepanelExpandedKey="application-document"
        footer={
          <>
            {newApplicationInfo?.status !== NewApplicationStatus.ACCEPTED &&
              newApplicationInfo?.status !== NewApplicationStatus.DECLINED &&
              documentItem && (
                <>
                  <ButtonPrimary
                    text={renameInProgress ? 'Loading...' : 'Save'}
                    onClick={onRename}
                    disabled={documentItem?.name === documentName}
                    bold
                  />
                  {documentItem?.status !==
                    ApplicationDocumentStatus.ACCEPTED &&
                    documentItem?.status !==
                      ApplicationDocumentStatus.EXPIRED &&
                    (isCorporate ||
                      (!isCorporate &&
                        !documentItem?.documentCategoryId?.includes(
                          'identification',
                        ))) &&
                    ((!isCorporate &&
                      (newApplicationInfo?.applicationScheme?.id ===
                        'ed560559-8354-4634-b6dd-eb341dfb551a' ||
                        newApplicationInfo?.applicationScheme?.id ===
                          'a87db4b4-3c78-4564-bba1-c16a19553159')) ||
                      isCorporate) && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === ApplicationDocumentStatus.ACCEPTED
                            ? 'Loading...'
                            : 'Accept'
                        }
                        onClick={() => {
                          setSelectedStatus(ApplicationDocumentStatus.ACCEPTED);
                          changeDocumentStatus(
                            ApplicationDocumentStatus.ACCEPTED,
                          );
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                  {documentItem?.status !==
                    ApplicationDocumentStatus.REJECTED &&
                    documentItem?.status !==
                      ApplicationDocumentStatus.EXPIRED &&
                    !documentItem?.documentCategoryId?.includes(
                      'identification',
                    ) &&
                    newApplicationInfo?.applicationScheme?.id ===
                      'ed560559-8354-4634-b6dd-eb341dfb551a' && (
                      <ButtonSecondary
                        text={
                          statusChangeInProgress &&
                          selectedStatus === ApplicationDocumentStatus.REJECTED
                            ? 'Loading...'
                            : 'Reject'
                        }
                        className={cn.rejectStatusButton}
                        onClick={() => {
                          setSelectedStatus(ApplicationDocumentStatus.REJECTED);
                          changeDocumentStatus(
                            ApplicationDocumentStatus.REJECTED,
                          );
                        }}
                        disabled={statusChangeInProgress}
                        bold
                      />
                    )}
                </>
              )}
            <ButtonPseudo
              text="Close"
              onClick={handleClose}
              className={`${cn.closeDocumentSidepanel} button-white`}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <div id="delete-popup-wrapper" />
    </>
  );
};

export default DocumentPanel;
